import { useState, useRef, useEffect, useCallback, type MouseEvent } from 'react';
import {
  MdThumbUp,
  MdThumbUpOffAlt,
  MdShare,
  MdCheck,
  MdFavoriteBorder,
  MdFavorite
} from 'react-icons/md';
import { useVideoDetailInfo } from './useVideoDetailInfo';
import { VideoDetailInfoProps } from './types';
import styles from './VideoDetailInfo.module.css';
import kebabMenuIcon from '../common/KebabMenu/kebab-menu-icon.svg';

/**
 * 動画の詳細情報を表示するコンポーネント
 */
const VideoDetailInfo = ({
  video,
  likesCount,
  uploaderIconUrl,
  hospitalName,
  uploaderName,
  tagArray: _tagArray,
  handleTagClick,
  kebabMenuItems,
  likeActive = false,
  favoriteActive = false,
  onLike,
  onFavorite,
  onCopyLink,
  isCopied = false,
  disabled = false,
  isOwner = false
}: VideoDetailInfoProps) => {
  const {
    titleExpanded,
    setTitleExpanded,
    isSP,
    displayedTitle,
    isTitleLong,
    relativeTime,
    formattedSurgeryDate,
    formattedLikesCount,
  } = useVideoDetailInfo(video, handleTagClick, likesCount);

  // ケバブメニューの開閉状態
  const [isKebabOpen, setIsKebabOpen] = useState(false);
  const kebabMenuRef = useRef<HTMLDivElement | null>(null);
  const kebabContainerRef = useRef<HTMLDivElement | null>(null);

  const toggleKebabMenu = useCallback((e: MouseEvent): void => {
    e.stopPropagation();
    setIsKebabOpen(prev => !prev);
  }, []);

  // メニュー外クリックで閉じる（トリガーボタンも含むコンテナ全体を除外）
  useEffect(() => {
    if (!isKebabOpen) return;
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (kebabContainerRef.current && kebabContainerRef.current.contains(target)) {
        return;
      }
      setIsKebabOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isKebabOpen]);

  // スクロール時にメニューを閉じる
  useEffect(() => {
    if (!isKebabOpen) return;
    const handleScroll = () => setIsKebabOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => { window.removeEventListener('scroll', handleScroll, true); };
  }, [isKebabOpen]);

  const hasKebabItems = kebabMenuItems && kebabMenuItems.length > 0;

  return (
    <div className={styles['VideoDetailInfo']}>
      {/* タイトル */}
      <div className={styles['VideoDetailInfo-titleContainer']}>
        <div className={styles['VideoDetailInfo-titleMain']}>
          <p
            className={`${styles['VideoDetailInfo-title']} ${
              isSP ? styles['VideoDetailInfo-title--sp'] : styles['VideoDetailInfo-title--pc']
            }`}
            data-testid="video-detail__title"
          >
            {displayedTitle}
            {isTitleLong && (
              <button
                type="button"
                className={styles['VideoDetailInfo-toggleTitle']}
                onClick={() => setTitleExpanded(!titleExpanded)}
              >
                {titleExpanded ? '折りたたむ' : '全文を表示する'}
              </button>
            )}
          </p>
        </div>
        {hasKebabItems && (
          <div ref={kebabContainerRef} className={styles['VideoDetailInfo-kebabContainer']}>
            <button
              className={styles['VideoDetailInfo-kebabButton']}
              onClick={toggleKebabMenu}
              aria-label="メニュー"
            >
              <img src={kebabMenuIcon} alt="" aria-hidden="true" />
            </button>
            {isKebabOpen && (
              <div
                ref={kebabMenuRef}
                className={styles['VideoDetailInfo-kebabDropdown']}
              >
                {kebabMenuItems.map((item, idx) => (
                  <button
                    key={item.label}
                    className={`${styles['VideoDetailInfo-kebabItem']} ${item.danger ? styles['VideoDetailInfo-kebabItem--danger'] : ''}`}
                    onClick={() => {
                      setIsKebabOpen(false);
                      item.onClick();
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* アップローダー情報行: アイコン + テキスト(左) + アクションボタン(右) */}
      <div className={styles['VideoDetailInfo-uploaderRow']}>
        <div className={styles['VideoDetailInfo-uploaderLeft']}>
          <img
            className={styles['VideoDetailInfo-uploaderIcon']}
            src={uploaderIconUrl}
            alt="アップローダーのアイコン"
          />
          <div className={styles['VideoDetailInfo-uploaderMeta']}>
            <div className={styles['VideoDetailInfo-hospitalRow']}>
              <span className={styles['VideoDetailInfo-hospitalName']}>{hospitalName}</span>
              {isOwner && (
                <span className={styles['VideoDetailInfo-uploaderName']}>{uploaderName}</span>
              )}
            </div>
            <div className={styles['VideoDetailInfo-metaLine']}>
              <span>投稿日:{relativeTime}</span>
              {formattedSurgeryDate && (
                <>
                  <span className={styles['VideoDetailInfo-metaSep']}>|</span>
                  <span>手術日:{formattedSurgeryDate}</span>
                </>
              )}
              {formattedLikesCount && (
                <>
                  <span className={styles['VideoDetailInfo-metaSep']}>|</span>
                  <span>{formattedLikesCount}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* アクションボタン群 */}
        <div className={styles['VideoDetailInfo-actions']}>
          <button
            className={`${styles['VideoDetailInfo-actionBtn']} ${
              likeActive ? styles['VideoDetailInfo-actionBtn--likeActive'] : ''
            }`}
            onClick={disabled ? undefined : onLike}
            disabled={disabled}
            title="いいね"
            data-testid="video-detail__like-button"
          >
            {likeActive ? <MdThumbUp size={18} /> : <MdThumbUpOffAlt size={18} />}
            <span>いいね</span>
          </button>
          <button
            className={`${styles['VideoDetailInfo-actionBtn']} ${
              isCopied ? styles['VideoDetailInfo-actionBtn--copied'] : ''
            }`}
            onClick={disabled ? undefined : onCopyLink}
            disabled={disabled}
            title="共有"
          >
            {isCopied ? <MdCheck size={18} /> : <MdShare size={18} />}
            <span>共有</span>
          </button>
          <button
            className={`${styles['VideoDetailInfo-actionBtn']} ${
              favoriteActive ? styles['VideoDetailInfo-actionBtn--favActive'] : ''
            }`}
            onClick={disabled ? undefined : onFavorite}
            disabled={disabled}
            title="お気に入り"
            data-testid="video-detail__favorite-button"
          >
            {favoriteActive ? <MdFavorite size={18} /> : <MdFavoriteBorder size={18} />}
            <span>お気に入り</span>
          </button>
        </div>
      </div>

    </div>
  );
};

export default VideoDetailInfo;
